import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api, keys } from "@shared/api";
import { TagChip } from "@features/server-tag";
import { ThreadMenuOn } from "@features/chat";
import { Markup } from "@shared/ui/Markup";
import { mentionsMe, plainText } from "@shared/lib/markup";
import { ago, dayTime, extension, fileSize, imageBox, plural, posted, stamp, time } from "@shared/lib/format";
import { fromSelf, livePerson } from "@entities/user";
import { useMe } from "@entities/session";
import { useCatalogue } from "@entities/workspace";
import { FileImage, copyImage, downloadFile, fileText, mediaObjectUrl } from "@shared/ui/FileImage";
import { ServerEmojiPicker } from "./ServerEmojiPicker";
import {
  isJumbo,
  quickEmoji,
  rememberEmoji,
  settleQuick,
  watchRecent,
  type CustomEmoji,
} from "@shared/lib/emoji";
import { Tile } from "@entities/user";
import type { Message, MessageAttachment, MessageForward, MessageReaction, Named, Uuid } from "@shared/api";
import { MenuOn, SEPARATOR, withId, type MenuItem } from "@shared/ui/Menu";
import { ContextMenuItem } from "@shared/ui/context-menu";
import { Dialog, Hint } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { messageLink, readInviteCodes, type MessageLink } from "@shared/lib/jump";
import { useShiftHeld } from "@shared/lib/shift";
import { LinkEmbeds } from "./LinkEmbeds";
import { ForwardDialog } from "./ForwardDialog";
import { ReactionsDialog, reactorsLine, useReactors } from "./ReactionsDialog";
import { messageMenu } from "../lib/message-menu";
import { messageBar, type BarAction } from "../lib/message-bar";
import { PollCard } from "./PollCard";
import { InviteCard } from "./InviteCard";
import { ToolbarHost } from "./toolbar-host";
import { RESPOND_WITHIN, useInteractions, type Call } from "../model/interactions";
import { BotComponents, BotEmbeds } from "./BotParts";
import { appItems, useChannelCommands } from "../model/commands";
import { Textarea } from "@shared/ui/textarea";
import {
  IconBookmark,
  IconClose,
  IconDown,
  IconEdit,
  IconEmoji,
  IconForward,
  IconLink,
  IconMarkUnread,
  IconMore,
  IconPinned,
  IconQuoted,
  IconReply,
  IconThreads,
  IconTrash,
  IconInvite,
  IconCall,
  IconSilent,
  IconPlay,
  IconPause,
  IconExpand,
  IconAnnouncementChannel,
  IconCommand,
  IconEye,
} from "@shared/ui/icons";
import { Spinner } from "@shared/ui/spinner";
import { isViewable, useMediaViewer, type MediaItem } from "@shared/ui/MediaViewer";
import { canSpeak, decodeWave, speak } from "@shared/media";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@shared/ui/popover";

interface RowProps {
  message: Message;
  /** Первое сообщение серии: у него шапка с именем и плашкой. */
  head: boolean;
  own: boolean;
  /** Есть ли право удалять чужое. Своё удаляет автор всегда. */
  canDeleteOthers: boolean;
  /** Сохранить правку: текст и какие вложения убрать. */
  onEdit: (content: string, removeAttachments?: Uuid[]) => Promise<void>;
  onDelete: () => void;
  /** Профиль автора: карточка рядом с именем или лицом, по которому щёлкнули. */
  onOpenProfile: (userId: string, anchor?: HTMLElement) => void;
  /** Имя по хэндлу: упоминание показывается именем, а хранится хэндлом. */
  nameOf: (handle: string) => string | null;
  /** Свой хэндл — по нему видно, что назвали именно нас. */
  mine: string;
  /** Сообщение побывало на глазах: под курсором или в видимой части ленты. */
  onSeen: (messageId: string) => void;
  /** Поставить или снять свой значок. */
  onReact: (emoji: string, mine: boolean) => void;
  /** Начать ответ на это сообщение. */
  onReply: () => void;
  /** Перейти к оригиналу, на который отвечает это сообщение. */
  onJumpTo: (messageId: string) => void;
  /** Можно ли ставить реакции в этом канале. */
  canReact: boolean;
  /** Можно ли писать: без этого и отвечать не на что. */
  canSend: boolean;
  /** Чей канал: из него и номера сообщения собирается ссылка. `null` — личная переписка. */
  serverId?: Uuid | null;
  /** Перейти к сообщению по ссылке из текста. */
  onOpenMessage?: (link: MessageLink) => void;
  /** Цвет имени автора — старшей цветной роли, как в Discord. */
  authorColor?: string | null;
  /** Значок старшей роли со значком — эмодзи после имени. */
  authorIcon?: string | null;
  /** Ник автора на этом сервере — вместо общего имени. */
  authorNick?: string | null;
  /** Сменился — открыть правку: так её просит ↑ в поле ввода. */
  editNow?: number;
  /** Правка кончилась — сохранили или отменили. */
  onEditDone?: () => void;
  /** Можно ли закреплять: в ветке нельзя вовсе, на сервере — по праву. */
  canPin?: boolean;
  /** Закрепить или открепить — смотря что сейчас. */
  onPin?: () => void;
  /** Разослать объявление подписчикам; нет — сообщение не из объявлений или уже разослано. */
  onPublish?: () => void;
  /** В своих сохранённых. */
  saved?: boolean;
  /** Сохранить или убрать из сохранённых; нет — пункта нет. */
  onSave?: () => void;
  /** О сообщении стоит своё напоминание. */
  reminded?: boolean;
  /** «Создать напоминание»: срок или `null` — «Другое…». Нет — пункта нет. */
  onRemind?: (at: Date | null) => void;
  /** «Удалить напоминание». */
  onForget?: () => void;
  /** «Отметить непрочитанным» с этого сообщения; нет — в меню пункта нет. */
  onMarkUnread?: () => void;
  /** Помахать новичку — ответ «👋» на приветствие, как в Discord. */
  onWave?: () => void;
  /** Голос в опросе этого сообщения; нет — опрос только для чтения. */
  onVote?: (answerIds: Uuid[]) => Promise<void>;
  /** «Завершить опрос» — автору и тем, кто управляет сообщениями. */
  onEndPoll?: () => Promise<void>;
  /** Открыть сервер (и канал) — после вступления по карточке приглашения. */
  onOpenServer?: (serverId: Uuid, channelId: Uuid | null) => void;
  /** Открыть ветку этого сообщения. `threadId` пуст — ветки ещё нет. */
  onOpenThread: (threadId: string | null) => void;
  /**
   * Заводить ветки можно: в личных сообщениях нельзя, как в Discord. Уже
   * заведённая ветка всё равно открывается — её ответы не должны пропасть.
   */
  threads?: boolean;
  /** Ветка этого сообщения открыта справа: корень подсвечен. */
  threadOpen: boolean;
  /** Где висит сообщение: меню ветки под ним обращается к каналу. */
  channelId: Uuid;
  /** Знаки этого сервера: и для показа в тексте, и для панели выбора. */
  custom: CustomEmoji[];
  /** Знак сервера по имени. `null` — такого здесь нет. */
  emojiOf: (name: string) => CustomEmoji | null;
  /** Имя канала по номеру — им рисуется упоминание через «#». */
  channelOf: (id: string) => string | null;
  roleOf: (id: string) => string | null;
  /** Свои роли: через них тоже зовут, и подсветить это надо так же. */
  myRoles: Uuid[];
  /** Нажали на упоминание канала. */
  onOpenChannel: (channelId: Uuid) => void;
  /**
   * Кого знаем по имени: состав сервера, друзья, собеседники. Автор строки
   * берётся отсюда, а не из снимка в самом сообщении.
   */
  people: Map<Uuid, Named>;
}

export function MessageRow({
  message,
  head,
  own,
  canDeleteOthers,
  onEdit,
  onDelete,
  onOpenProfile,
  nameOf,
  mine,
  onSeen,
  onReact,
  onReply,
  onJumpTo,
  canReact,
  canSend,
  serverId = null,
  onOpenMessage,
  authorColor = null,
  authorIcon = null,
  authorNick = null,
  editNow = 0,
  onEditDone,
  canPin = false,
  onPin,
  onPublish,
  saved = false,
  onSave,
  reminded = false,
  onRemind,
  onForget,
  onMarkUnread,
  onWave,
  onVote,
  onEndPoll,
  onOpenServer,
  onOpenThread,
  threads = true,
  threadOpen,
  channelId,
  custom,
  emojiOf,
  channelOf,
  roleOf,
  myRoles,
  onOpenChannel,
  people,
}: RowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const [busy, setBusy] = useState(false);
  const [editProblem, setEditProblem] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  /** Спрашиваем, точно ли удалить. */
  const [confirming, setConfirming] = useState(false);
  /** Открыто окно «Переслать». */
  const [forwarding, setForwarding] = useState(false);
  /** Выделенное в строке на момент правого щелчка: из него пункт «Копировать». */
  const [selection, setSelection] = useState("");
  /*
   * Курсор над строкой.
   *
   * Признак нужен не для вида — вид даёт CSS, — а для того, чтобы кнопок ряда
   * не было в разметке вовсе, пока на строку не навели. Их девять, каждая со
   * значком, и это больше трети узлов строки: в ленте из трёхсот они
   * складываются в лишние тысячи. Прятать их `display: none`, как было, дёшево
   * для мысли, но не для браузера.
   */
  const [hovered, setHovered] = useState(false);
  const host = useContext(ToolbarHost);
  const row = useRef<HTMLDivElement>(null);
  const tool = useRef<HTMLDivElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  // Свой профиль отдельно от списков: в переписке себя нет ни в друзьях, ни
  // в составе, а своя смена аватара заметнее всего.
  const me = fromSelf(useMe());
  // Ник на сервере стоит вместо общего имени везде в строке — и в инициалах.
  const live = livePerson(message.author, people, me);
  const titled = authorNick ? { ...live, name: authorNick } : live;
  // Вебхук пишет под именем и аватаром этого сообщения, если их прислали.
  const masquerade = message.masquerade;
  const author = masquerade
    ? { ...titled, name: masquerade.name ?? titled.name, avatarId: masquerade.avatar_id ?? titled.avatarId }
    : titled;

  /*
   * Пятёрка для ряда быстрых реакций.
   *
   * Через подписку, а не разовым чтением: порядок общий на всю ленту, и
   * сменившись, он обязан смениться сразу везде, а не в той одной строке,
   * где нажали.
   */
  const quick = useSyncExternalStore(watchRecent, quickEmoji);

  /** Поставить или снять знак и запомнить его в недавних. */
  function react(emoji: string) {
    onReact(
      emoji,
      message.reactions.some((item) => item.emoji === emoji && item.mine),
    );
    rememberEmoji(emoji);
  }

  useEffect(() => {
    if (editing) {
      area.current?.focus();
      area.current?.setSelectionRange(draft.length, draft.length);
    }
    /*
     * Только на включение правки: каретка ставится в конец один раз, а `draft`
     * меняется на каждую букву — брать его сюда значит возвращать каретку в
     * конец посреди набора.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  /**
   * Удаление — с вопросом, как в Discord: вернуть сообщение нельзя. Shift+клик
   * удаляет сразу — тому, кто чистит ленту, вопрос на каждое мешает.
   */
  function askDelete(skip: boolean) {
    if (skip) onDelete();
    else setConfirming(true);
  }

  // Голосовое и опрос не правят — как в Discord: у первого нет текста, второй неизменен.
  const editable = own && !message.poll && !message.attachments.some((attachment) => attachment.waveform);

  /** Открыто ли окно «Реакции». */
  const [reactionsOpen, setReactionsOpen] = useState(false);

  /** Картинка, по которой щёлкнули правой кнопкой: меню начинается с её пунктов. */
  const [imageAt, setImageAt] = useState<Uuid | null>(null);

  /** Какие вложения убираются этой правкой — крестиком, как в Discord. */
  const [dropped, setDropped] = useState<ReadonlySet<Uuid>>(new Set());

  function startEdit() {
    setDraft(message.content);
    setDropped(new Set());
    setEditing(true);
  }

  function stopEdit() {
    setEditing(false);
    setDraft(message.content);
    setDropped(new Set());
    onEditDone?.();
  }

  useEffect(() => {
    if (editNow && editable) startEdit();
    // Только на просьбу: текст сообщения меняется и без неё.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editNow]);

  async function commit() {
    const next = draft.trim();
    const removing = message.attachments.filter((item) => dropped.has(item.id)).map((item) => item.id);
    const left = message.attachments.length - removing.length;

    // Пустым сообщение не остаётся: без текста оно держится только на вложениях.
    if (!next && left === 0 && !message.sticker) {
      if (removing.length === 0) stopEdit();
      else setEditProblem("Сообщение не может остаться пустым — удалите его целиком");
      return;
    }
    if (next === message.content && removing.length === 0) {
      stopEdit();
      return;
    }

    setBusy(true);
    setEditProblem(null);
    try {
      await onEdit(next, removing);
      setEditing(false);
      onEditDone?.();
    } catch (error) {
      setEditProblem(
        error instanceof ApiError && error.code === "automod"
          ? "АвтоМод сервера не пропустил слово"
          : error instanceof ApiError
            ? error.message
            : "Правка не сохранилась",
      );
    } finally {
      setBusy(false);
    }
  }

  // Сообщение, где назвали лично, отмечено целиком: в длинной ленте искать
  // подсвеченное слово глазами дольше, чем увидеть полосу у края.
  // Позвали по имени, через роль или ответом с «@ВКЛ» — по списку с сервера.
  const named = mentionsMe(message.content, mine, myRoles) || (message.mentions ?? []).includes(me.id);

  /*
   * Ряд действий — как в Discord: одна капсула на подложке «Почты», спереди
   * три частых знака, за чертой — действия, нужные этому сообщению. Какие —
   * решает `messageBar`; с Shift ряд раскрывается.
   */
  const showBar = hovered || picking;
  const expanded = useShiftHeld(showBar) && !picking;
  // Эфемерное не пересылают, опрос — тоже, как в Discord.
  const forwardable = !message.ephemeral && !message.poll;
  const layout = messageBar({
    expanded,
    canReact,
    canSend,
    editable,
    forwardable,
    canThread: threads,
    hasThread: !!message.thread,
    canPin: canPin && !!onPin,
    canSave: !!onSave,
    canMarkUnread: !!onMarkUnread,
    canDelete: own || canDeleteOthers,
    canPublish: !!onPublish,
  });
  const copyLink = () =>
    void navigator.clipboard?.writeText(messageLink({ serverId, channelId, messageId: message.id })).catch(() => {});

  function action(key: BarAction) {
    const plain = (title: string, icon: React.ReactNode, onClick: (event: React.MouseEvent) => void, danger = false) => (
      <button key={key} type="button" className={`msg-action ${danger ? "danger" : ""}`} title={title} onClick={onClick}>
        {icon}
      </button>
    );
    switch (key) {
      case "copy-link":
        return plain("Скопировать ссылку", <IconLink size={18} />, copyLink);
      case "mark-unread":
        return plain("Отметить непрочитанным", <IconMarkUnread size={18} />, () => onMarkUnread?.());
      case "save":
        return plain(saved ? "Убрать из сохранённых" : "Сохранить", <IconBookmark size={18} fill={saved ? "currentColor" : "none"} />, () => onSave?.());
      case "pin":
        return plain(message.pinned ? "Открепить" : "Закрепить", <IconPinned size={18} />, () => onPin?.());
      case "thread":
        return plain("Создать ветку", <IconThreads size={18} />, () => onOpenThread(null));
      case "view-thread":
        return plain("Показать ветку", <IconThreads size={18} />, () => onOpenThread(message.thread?.id ?? null));
      case "reply":
        return plain("Ответить", <IconReply size={18} />, onReply);
      case "edit":
        return plain("Изменить", <IconEdit size={18} />, startEdit);
      case "forward":
        return plain("Переслать", <IconForward size={18} />, () => setForwarding(true));
      case "publish":
        return plain("Опубликовать", <IconAnnouncementChannel size={18} />, () => onPublish?.());
      // Shift зажат — значит, без вопроса, как Shift+клик в Discord.
      case "delete":
        return plain("Удалить", <IconTrash size={18} />, (event) => askDelete(event.shiftKey), true);
      case "react":
        return (
          <PopoverTrigger asChild key={key}>
            <button type="button" className={`msg-action ${picking ? "on" : ""}`} title="Добавить реакцию">
              <IconEmoji size={18} />
            </button>
          </PopoverTrigger>
        );
      /*
       * «Ещё» — то же меню, что по правой кнопке: о ней знают не все, а кнопку
       * видно. Меню открывается тем же событием, что и правой кнопкой, у самой
       * кнопки, — второго меню заводить не за чем.
       */
      case "more":
        return plain("Ещё", <IconMore size={18} />, (event) => {
          const box = event.currentTarget.getBoundingClientRect();
          row.current?.dispatchEvent(
            new MouseEvent("contextmenu", {
              bubbles: true,
              cancelable: true,
              clientX: box.left,
              clientY: box.bottom + 4,
            }),
          );
        });
    }
  }

  /*
   * Вынесенный ряд сам ходит за строкой: лента прокручивается, а слой над ней —
   * нет. Правый край ряда — у правого края строки; сдвиг на свою ширину берёт
   * `-100%`, так что мерить ряд не нужно, а раскрытие с Shift растёт влево.
   */
  useLayoutEffect(() => {
    if (!host || !showBar) return;
    let raf = 0;
    const follow = () => {
      const r = row.current?.getBoundingClientRect();
      const b = host.getBoundingClientRect();
      if (r && tool.current) {
        tool.current.style.transform = `translate(calc(${Math.round(r.right - b.left - 10)}px - 100%), ${Math.round(r.top - b.top - 22)}px)`;
      }
      raf = requestAnimationFrame(follow);
    };
    follow();
    return () => cancelAnimationFrame(raf);
  }, [host, showBar]);

  /*
   * Ряд появляется сразу на месте: он показывается на каждой строке под
   * курсором, и любое раскрытие при движении мыши по ленте давало бы
   * сплошное движение.
   *
   * Панель знаков — та же самая, что открывается из поля ввода. Прижата к
   * правому краю ряда действий: открываться она должна там, где нажали, а не
   * посреди ленты. Потому опора — ряд, а не кнопка.
   *
   * Начинка держится не только на наведении, но и на открытой панели: та
   * уехала в портал, курсор над ней — уже не над строкой, и без оговорки ряд
   * исчезал бы прямо из-под открытого выбора.
   */
  const bar = (
    <PopoverAnchor asChild>
      <div ref={tool} className={`msg-actions ${picking ? "on" : ""} ${host ? "floating" : ""}`}>
        {showBar && (
          <div className="msg-bar surface-pop" role="toolbar" aria-label="Действия с сообщением">
            {layout.quick && (
              <>
                {/*
                  Три частых знака — прямо в ряду, как в Discord. Ответить жестом
                  чаще хотят, чем править и удалять, и прятать это за вторым
                  нажатием значит платить лишним движением за самое частое.
                */}
                {quick.slice(0, 3).map((emoji) => (
                  <button key={emoji} type="button" className="msg-quick" title={emoji} onClick={() => react(emoji)}>
                    {emoji}
                  </button>
                ))}
                <span className="msg-bar-split" aria-hidden />
              </>
            )}
            {layout.actions.map(action)}
          </div>
        )}
      </div>
    </PopoverAnchor>
  );

  /*
   * Команды ботов на сообщение — подменю «Приложения»; эфемерному их не дают.
   * Спрашиваем, когда к строке потянулись: курсором или долгим нажатием, — а не
   * на каждую открытую ленту.
   */
  const [wantsApps, setWantsApps] = useState(false);
  const apps = useChannelCommands(serverId ? channelId : null, !message.ephemeral && (hovered || wantsApps));
  const textMenu = messageMenu({
    apps: message.ephemeral ? [] : appItems(apps, 3, channelId, message.id, message.thread_id ?? null),
    own,
    editable,
    forwardable,
    canDeleteOthers,
    canReact,
    canSend,
    canPin: canPin && !!onPin,
    pinned: message.pinned,
    saved,
    reminded,
    hasThread: !!message.thread,
    content: message.content,
    selection,
    /*
     * Правка и ответ ставят фокус в поле, а меню, пока закрывается, держит его
     * у себя и забирает назад. Поэтому они — после закрытия.
     */
    actions: {
      pickEmoji: () => setPicking(true),
      edit: () => setTimeout(startEdit),
      reply: () => setTimeout(onReply),
      thread: threads || message.thread ? () => onOpenThread(message.thread?.id ?? null) : undefined,
      copy: (text) => void navigator.clipboard?.writeText(text).catch(() => {}),
      copyLink,
      pin: () => onPin?.(),
      publish: onPublish,
      save: onSave,
      remind: onRemind,
      forget: onForget,
      // После закрытия меню: окно и уходящее меню делили бы фокус.
      reactions: message.reactions.length > 0 ? () => setTimeout(() => setReactionsOpen(true)) : undefined,
      speak: message.content.trim() && canSpeak() ? () => speak(`${author.name}: ${plainText(message.content)}`) : undefined,
      markUnread: onMarkUnread,
      // После закрытия меню — как и удаление: окно и уходящее меню делили бы фокус.
      forward: () => setTimeout(() => setForwarding(true)),
      // После закрытия меню: окно с вопросом и уходящее меню делили бы фокус.
      remove: () => setTimeout(() => askDelete(false)),
    },
  });

  /*
   * Правый щелчок по картинке — сверху её пункты, как в Discord: скопировать
   * в буфер и сохранить файлом. Какую картинку нажали, ловится на погружении
   * события, раньше, чем меню соберётся.
   */
  const pickedImage = imageAt
    ? [...message.attachments, ...(message.forwarded?.attachments ?? [])].find((item) => item.id === imageAt)
    : undefined;
  const menu: MenuItem[] = pickedImage
    ? [
        { label: "Скопировать изображение", onPick: () => void copyImage(pickedImage.id).catch(() => {}) },
        { label: "Сохранить изображение", onPick: () => void downloadFile(pickedImage.id, pickedImage.filename) },
        SEPARATOR,
        ...textMenu,
      ]
    : textMenu;

  /*
   * Системное — «на сервере пополнение», как в Discord. Строка короткая, без
   * лица и меню; подпись выбирается по номеру сообщения, чтобы у одного и того
   * же входа она не менялась от перерисовки. Без рода: местоимения человека
   * продукт не знает. Наблюдатель за лентой находит и её — по `data-message`.
   */
  /*
   * Звонок в переписке — как в Discord: пока идёт — «начинает звонок» и
   * «Присоединиться» тому, кого в нём нет; кончился — сколько длился, а не
   * успевшему войти — «пропущенный звонок». Без рода, как и приветствие.
   */
  if (message.kind === "call") {
    const log = message.call;
    const ended = log?.ended ?? null;
    const missed = !!ended && !log?.participants.includes(me.id);
    const system = withId(
      [
        ...(onMarkUnread ? [{ label: "Отметить непрочитанным", onPick: onMarkUnread }] : []),
        ...(own || canDeleteOthers ? [{ label: "Удалить", kind: "danger" as const, onPick: onDelete }] : []),
      ] satisfies MenuItem[],
      message.id,
    );
    return (
      <MenuOn title="ЗВОНОК" items={system} disabled={system.length === 0}>
        <div
          className={`msg msg-system msg-call head ${missed ? "missed" : ""}`}
          data-message={message.id}
          onMouseEnter={() => onSeen(message.id)}
        >
          <IconCall size={15} className="msg-system-icon" />
          <span className="grow">
            {!ended ? (
              <>
                <b>{author.name}</b> начинает звонок.
                <JoinCallLink channelId={message.channel_id} meId={me.id} />
              </>
            ) : missed ? (
              <>
                Пропущенный звонок от <b>{author.name}</b> — {callLength(message.created, ended)}.
              </>
            ) : (
              <>
                Звонок от <b>{author.name}</b> — {callLength(message.created, ended)}.
              </>
            )}
          </span>
          <span className="msg-time" title={dayTime(message.created)}>
            {time(message.created)}
          </span>
        </div>
      </MenuOn>
    );
  }

  if (message.kind === "join") {
    // Меню короткое: отметить непрочитанным и убрать (своё или с правом модератора).
    const system = withId(
      [
        ...(onMarkUnread ? [{ label: "Отметить непрочитанным", onPick: onMarkUnread }] : []),
        ...(own || canDeleteOthers ? [{ label: "Удалить", kind: "danger" as const, onPick: onDelete }] : []),
      ] satisfies MenuItem[],
      message.id,
    );
    return (
      <MenuOn title="СИСТЕМНОЕ" items={system} disabled={system.length === 0}>
        <div className="msg msg-system head" data-message={message.id} onMouseEnter={() => onSeen(message.id)}>
          <IconInvite size={15} className="msg-system-icon" />
          <span className="grow">{welcome(author.name, message.id)}</span>
          {onWave && canSend && !own && (
            <button type="button" className="msg-wave" onClick={onWave}>
              👋 Помахать
            </button>
          )}
          <span className="msg-time" title={dayTime(message.created)}>
            {time(message.created)}
          </span>
        </div>
      </MenuOn>
    );
  }

  return (
    <>
    {/*
     * Меню по правой кнопке — те же действия, что в ряду, и копирование.
     * Пока строку правят, меню нет: в поле правки нужно родное, с вставкой.
     * Окно с вопросом об удалении — рядом, а не внутри: щелчки из него
     * всплывали бы в строку и открывали её меню.
     */}
    <MenuOn
      title={author.name}
      sub={dayTime(message.created)}
      items={withId(menu, message.id)}
      disabled={editing}
      onContextMenuCapture={(event) =>
        setImageAt((event.target as HTMLElement).closest<HTMLElement>("[data-attachment]")?.dataset.attachment ?? null)
      }
      head={
        canReact && (
          <div className="msg-menu-quick">
            {quick.map((emoji) => (
              <ContextMenuItem key={emoji} className="msg-menu-emoji" onSelect={() => react(emoji)}>
                {emoji}
              </ContextMenuItem>
            ))}
          </div>
        )
      }
    >
      <div
        ref={row}
        className={`msg ${head ? "head" : ""} ${named ? "named" : ""} ${threadOpen ? "rooted" : ""}`}
        /* По номеру его находит наблюдатель за видимой частью ленты. */
        data-message={message.id}
        onMouseEnter={() => {
          onSeen(message.id);
          setHovered(true);
        }}
        /*
         * Курсор ушёл — ряд быстрых реакций можно переставить.
         *
         * Ряд показывается по наведению, и это единственное окно, когда его
         * порядок менять безопасно: переставленный под курсором, он уводит
         * мишень ровно в тот миг, когда по ней целятся вторым нажатием.
         */
        onMouseLeave={() => {
          setHovered(false);
          settleQuick();
        }}
        /* Выделенное снимается до того, как меню откроется: щелчок его не сбросит, а меню прочтёт. */
        onContextMenuCapture={() => {
          setWantsApps(true);
          const picked = window.getSelection();
          const text = picked && row.current?.contains(picked.anchorNode) ? picked.toString().trim() : "";
          setSelection(text);
        }}
        /* Alt+щелчок — «Отметить непрочитанным», как в Discord. Ссылки и кнопки в строке — своё. */
        onClick={(event) => {
          if (!event.altKey || !onMarkUnread || editing) return;
          if ((event.target as HTMLElement).closest("a, button, input, textarea")) return;
          event.preventDefault();
          onMarkUnread();
        }}
      >
        {/* Правка — только своя, даже у владельца сервера. Удаление чужого — по праву. */}
        <Popover open={picking} onOpenChange={setPicking}>
          {/*
            Панель — та же самая, что открывается из поля ввода. Прижата к
            правому краю ряда действий: открываться она должна там, где нажали, а
            не посреди ленты. Потому опора — ряд, а не кнопка.

            Сама коробка ряда поэтому остаётся всегда, даже пустая: опора,
            появляющаяся вместе с тем, что на ней открывают, — это опора из
            ниоткуда. Стоит она один узел.

            Начинка же держится не только на наведении, но и на открытой панели:
            та уехала в портал, курсор над ней — уже не над строкой, и без
            оговорки ряд исчезал бы прямо из-под открытого выбора.
          */}
          {host ? showBar && createPortal(bar, host) : bar}

          <PopoverContent
            side="bottom"
            align="end"
            sideOffset={4}
            aria-label="Выбор знака"
            /* Фокус — на поиск, а не на саму панель: набирают чаще, чем листают. */
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            <ServerEmojiPicker
              serverId={serverId}
              custom={custom}
              onPick={(value) => {
                react(value);
                setPicking(false);
              }}
            />
          </PopoverContent>
        </Popover>

        {head ? (
          <button
            type="button"
            className="msg-face"
            aria-label={`Профиль ${author.name}`}
            onClick={(event) => onOpenProfile(message.author.id, event.currentTarget)}
          >
            <Tile person={author} size={36} mark={null} />
          </button>
        ) : (
          <span className="msg-gutter" title={dayTime(message.created)}>
            {time(message.created)}
          </span>
        )}

        <div className="msg-body">
          {head && (
            <div className="msg-head">
              <button
                type="button"
                className={`msg-author ${own ? "own" : ""}`}
              style={authorColor ? { color: authorColor } : undefined}
                onClick={(event) => onOpenProfile(message.author.id, event.currentTarget)}
                title={message.author.handle}
              >
                {author.name}
              </button>
              {authorIcon && (
                <span className="msg-role-icon" aria-hidden>
                  {authorIcon}
                </span>
              )}
              {message.author.bot && <span className="bot-chip">БОТ</span>}
              <TagChip tag={message.author.tag} />
              {/*
                Метка — как в Discord: сегодня одно время, вчера «Вчера, в …»,
                раньше — число и время. Полная дата с днём недели — по наведению.
              */}
              <span className="msg-at" title={stamp(new Date(message.created), "F")}>
                {posted(message.created)}
              </span>
              {message.silent && (
                <span className="msg-silent" title="Тихое сообщение: без оповещений" aria-label="Тихое сообщение">
                  <IconSilent size={12} />
                </span>
              )}
            </div>
          )}

          {/* Ответ бота на вызов — как в Discord: над ним, кто какую команду вызвал. */}
          {message.interaction && (
            <div className="msg-quote msg-called">
              <span className="msg-quote-turn">
                <IconCommand size={13} />
              </span>
              <span className="msg-quote-name">{livePerson(message.interaction.user, people, me).name}</span>
              <span className="msg-quote-text ellipsis">{calledText(message.interaction)}</span>
            </div>
          )}

          {message.reply_to && (
            <button
              type="button"
              className="msg-quote"
              onClick={() => onJumpTo(message.reply_to!.id)}
              title="Перейти к сообщению"
            >
              <span className="msg-quote-turn"><IconQuoted size={13} /></span>
              {/* Тоже живое имя: иначе в строке новое, а в подписи над ней старое. */}
              <span className="msg-quote-name">
                {livePerson(message.reply_to.author, people, me).name}
              </span>
              <span className="msg-quote-text ellipsis">
                {message.reply_to.excerpt || (message.reply_to.has_attachments ? "вложение" : "")}
              </span>
              {message.reply_to.excerpt && message.reply_to.has_attachments && (
                <span className="msg-quote-clip">и вложение</span>
              )}
            </button>
          )}

          {editing ? (
            <div className="msg-edit">
              <Textarea
                ref={area}
                value={draft}
                disabled={busy}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void commit();
                  }
                  if (event.key === "Escape") stopEdit();
                }}
              />
              {/* Вложения при правке — плашками с крестиком, как в Discord. */}
              {message.attachments.length > 0 && (
                <div className="msg-edit-files">
                  {message.attachments.map((item) => {
                    const gone = dropped.has(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`msg-edit-file ${gone ? "gone" : ""}`}
                        title={gone ? "Вернуть вложение" : "Убрать вложение"}
                        onClick={() =>
                          setDropped((previous) => {
                            const next = new Set(previous);
                            if (gone) next.delete(item.id);
                            else next.add(item.id);
                            return next;
                          })
                        }
                      >
                        <span className="ellipsis">{item.filename}</span>
                        {gone ? <IconReply size={12} /> : <IconClose size={12} />}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="msg-edit-hint">
                Enter — сохранить, Esc — отменить
              </div>
              {editProblem && <div className="small danger-text">{editProblem}</div>}
            </div>
          ) : (
            message.content && (
              /*
               * Сообщение из одного–трёх знаков и ничего больше набирается
               * крупно: это ответ жестом, и мельчить тут незачем. С четвёртого
               * правило отключается — шесть знаков по 34 пункта уже плакат
               * посреди разговора.
               */
              <div className={`msg-text ${isJumbo(message.content) ? "jumbo" : ""}`}>
                <Markup
                  text={message.content}
                  nameOf={nameOf}
                  mine={mine}
                  emojiOf={emojiOf}
                  channelOf={channelOf}
                  roleOf={roleOf}
                  onOpenChannel={onOpenChannel}
                  onOpenMessage={onOpenMessage}
                />
                {/* В конце текста, а не отдельной строкой: не двигает ленту. */}
                {message.edited && (
                    <span className="msg-flag" title={`Изменено ${dayTime(message.edited)}`}>
                      (изменено)
                    </span>
                  )}
                {message.published && (
                  <span className="msg-flag" title="Разослано подписчикам канала">
                    (опубликовано)
                  </span>
                )}
              </div>
            )
          )}

          <MessageExtras
            message={message}
            editing={editing}
            people={people}
            nameOf={nameOf}
            mine={mine}
            emojiOf={emojiOf}
            channelOf={channelOf}
            roleOf={roleOf}
            onOpenChannel={onOpenChannel}
            onOpenMessage={onOpenMessage}
            onOpenServer={onOpenServer}
            onVote={onVote}
            onEndPoll={onEndPoll}
            canRemoveEmbeds={own || canDeleteOthers}
          />

          {/* Эфемерный ответ видит один вызвавший, и убрать его можно только у себя. */}
          {message.ephemeral && (
            <div className="msg-ephemeral">
              <IconEye size={12} /> Видно только вам ·{" "}
              <button
                type="button"
                className="link-btn"
                onClick={() => useInteractions.getState().dismiss(message.id)}
              >
                Скрыть
              </button>
            </div>
          )}

          {/* Как в Discord: неразосланное объявление — с кнопкой под ним. */}
          {onPublish && (
            <button type="button" className="msg-publish" onClick={onPublish}>
              <IconAnnouncementChannel size={13} /> Опубликовать
            </button>
          )}

          {message.reactions.length > 0 && (
            <div className="reactions">
              {message.reactions.map((reaction) => (
                <ReactionChip
                  key={reaction.emoji}
                  channelId={channelId}
                  messageId={message.id}
                  reaction={reaction}
                  // К уже стоящей реакции присоединяются и без права ставить новые — как в
                  // Discord; на тайм-ауте и в чужой переписке (писать нельзя) — нет.
                  disabled={!canReact && !canSend}
                  onToggle={() => react(reaction.emoji)}
                >
                  <ReactionMark emoji={reaction.emoji} emojiOf={emojiOf} />
                </ReactionChip>
              ))}
              {canReact && !message.ephemeral && <AddReaction serverId={serverId} custom={custom} onPick={react} />}
            </div>
          )}

          {/*
            Ветка — всегда последняя строка блока, после реакций. Порядок
            жёсткий и не зависит от содержимого: глаз ищет её в одном месте, а
            не там, где кончился текст.
          */}
          {message.thread && (
            <ThreadMenuOn
              thread={{
                channelId,
                threadId: message.thread.id,
                // Корень ветки — это само сообщение.
                rootId: message.id,
                ownerId: message.thread.owner_id,
                followed: message.thread.followed,
                notifyLevel: message.thread.notify_level,
                notifyUntil: message.thread.notify_until,
                name: message.thread.title,
                closed: message.thread.closed,
                locked: message.thread.locked,
                unread: message.thread.unread,
              }}
            >
              <button
                type="button"
                className={`thread-entry ${message.thread.unread > 0 ? "fresh" : ""}`}
                onClick={() => onOpenThread(message.thread!.id)}
              >
                {message.thread.unread > 0 && <span className="thread-dot" />}
                <span className="thread-faces">
                  {message.thread.participants.slice(0, 3).map((person) => (
                    <Tile
                      key={person.id}
                      person={livePerson(person, people, me)}
                      size={16}
                      mark={null}
                    />
                  ))}
                </span>
                <span className="thread-count">
                  {message.thread.reply_count}{" "}
                  {plural(message.thread.reply_count, "ответ", "ответа", "ответов")}
                </span>
                {message.thread.unread > 0 && (
                  <span className="thread-new">{message.thread.unread} новых</span>
                )}
                <span className="thread-when">
                  {message.thread.last_reply_at ? ago(message.thread.last_reply_at) : ""}
                </span>
              </button>
            </ThreadMenuOn>
          )}
        </div>
      </div>
    </MenuOn>
    {forwarding && <ForwardDialog message={message} onClose={() => setForwarding(false)} />}
    {reactionsOpen && (
      <ReactionsDialog
        channelId={channelId}
        messageId={message.id}
        reactions={message.reactions}
        mark={(emoji) => <ReactionMark emoji={emoji} emojiOf={emojiOf} />}
        onClose={() => setReactionsOpen(false)}
      />
    )}
    {confirming && (
      <Dialog
        title="Удалить сообщение?"
        onClose={() => setConfirming(false)}
        actions={
          <>
            <Button variant="secondary" type="button" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              type="button"
              onClick={() => {
                setConfirming(false);
                onDelete();
              }}
            >
              Удалить
            </Button>
          </>
        }
      >
        <div className="msg-doomed">
          <b>{author.name}</b>
          <span>
            {plainText(message.content) ||
              `${message.attachments.length} ${plural(message.attachments.length, "вложение", "вложения", "вложений")}`}
          </span>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          Вернуть его будет нельзя. Shift+клик по корзине удаляет без вопроса.
        </p>
      </Dialog>
    )}
    </>
  );
}


/**
 * Значок в строке реакций.
 *
 * Реакция хранится строкой, и кастомный знак приезжает в ней именем в
 * двоеточиях — тем же видом, что в тексте сообщения. Знака нет на этом
 * сервере — остаётся имя, а не пустая плашка: по нему видно, на что
 * отвечали.
 *
 * Кегль здесь равен кеглю окружающего текста, а не кеглю знака в строке:
 * отдельных размеров под реакции не заводим.
 */
/**
 * Значок под сообщением. По наведению — кто поставил, как в Discord: список
 * спрашивается при первом наведении, а не для каждой реакции ленты.
 */
function ReactionChip({
  channelId,
  messageId,
  reaction,
  disabled,
  onToggle,
  children,
}: {
  channelId: Uuid;
  messageId: Uuid;
  reaction: MessageReaction;
  disabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  const { data: people } = useReactors(channelId, messageId, reaction.emoji, hovered);
  return (
    <Hint side="top" label={hovered ? reactorsLine(people, reaction.count) : reaction.emoji}>
      <button
        type="button"
        className={`reaction ${reaction.mine ? "mine" : ""}`}
        disabled={disabled}
        aria-label={reaction.mine ? "Убрать свою реакцию" : "Присоединиться"}
        onClick={onToggle}
        onMouseEnter={() => setHovered(true)}
      >
        <span className="reaction-emoji">{children}</span>
        <span className="reaction-count">{reaction.count}</span>
      </button>
    </Hint>
  );
}

/**
 * «Добавить реакцию» в конце строки значков — как в Discord: раз реакции уже
 * есть, ещё одну ставят тут же, не ища ряда над сообщением. Видна всегда, а не
 * по наведению, тоже как там. Панель знаков та же, что у ряда, но открывается
 * справа от кнопки: там, где нажали.
 */
function AddReaction({
  serverId,
  custom,
  onPick,
}: {
  serverId: Uuid | null;
  custom: CustomEmoji[];
  onPick: (emoji: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`reaction reaction-add ${open ? "on" : ""}`}
          title="Добавить реакцию"
          aria-label="Добавить реакцию"
        >
          <IconEmoji size={16} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={6}
        aria-label="Выбор знака"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <ServerEmojiPicker
          serverId={serverId}
          custom={custom}
          onPick={(value) => {
            onPick(value);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function ReactionMark({
  emoji,
  emojiOf,
}: {
  emoji: string;
  emojiOf: (name: string) => CustomEmoji | null;
}) {
  const name = /^:([^:\s]+):$/.exec(emoji)?.[1];
  if (!name) return emoji;

  const known = emojiOf(name);
  if (!known) return <span className="emoji-absent">:{name}:</span>;

  return <FileImage id={known.file_id} alt={`:${name}:`} className="reaction-image" />;
}

/**
 * До какого размера показываем текст, в байтах.
 *
 * Не про безопасность, а про ленту: файл едет целиком и целиком ложится в
 * разметку. Шестьдесят четыре килобайта — это около полутора тысяч строк,
 * дальше смотреть в ленте всё равно нечего, и уместнее скачать.
 */
const PREVIEW_MAX = 64 * 1024;

/** Сколько строк показать. Остальное — числом в подписи. */
const PREVIEW_LINES = 20;

/**
 * Текстовое вложение с показом содержимого.
 *
 * Вид взят у блока кода в сообщении — тот же `mk-code`, та же подпись языка.
 * Своего вида здесь быть не должно: код в переписке и код в файле — одно и то
 * же, и два разных вида для него читались бы как два разных смысла.
 *
 * Подсветки нет, и это не забывчивость: она не заведена нигде, причина
 * записана у блока кода в `ui/Markup.tsx` — разборщик на каждый язык ради
 * редкой вставки. Подпись языка при этом честна: видно, чего ждать.
 */
function TextAttachment({ attachment }: { attachment: MessageAttachment }) {
  /*
   * Текст спрашивается кэшем запросов, а не эффектом с флагом «ещё жив».
   *
   * Ключ у вопроса свой, и это снимает сразу две вещи: гонку при быстрой
   * прокрутке (ответ на прошлый файл больше некуда положить) и повторный
   * запрос за тем же файлом — он встречается в ленте не раз.
   */
  const { data: text = null, isError: failed } = useQuery({
    queryKey: keys.fileText(attachment.id),
    queryFn: () => fileText(attachment.id),
    staleTime: Infinity,
  });

  const lines = text === null ? [] : text.split("\n");
  const shown = lines.slice(0, PREVIEW_LINES);
  const rest = lines.length - shown.length;
  const lang = extension(attachment.filename, attachment.content_type).toLowerCase();

  return (
    <div className="attach-text">
      {/* Не достали — показываем одну карточку, как у прочих файлов. */}
      {!failed && (
        <div className="mk-code">
          <span className="mk-code-lang">{lang}</span>
          <pre>{text === null ? "…" : shown.join("\n")}</pre>
        </div>
      )}

      <button
        type="button"
        className="file-card"
        onClick={() => void downloadFile(attachment.id, attachment.filename)}
      >
        <span className="file-ext">{extension(attachment.filename, attachment.content_type)}</span>
        <span style={{ minWidth: 0 }}>
          <span className="file-name ellipsis">{attachment.filename}</span>
          <span className="file-meta">
            {fileSize(attachment.size)}
            {rest > 0 && ` · ещё ${rest} ${plural(rest, "строка", "строки", "строк")}`}
            {failed && " · показать не вышло"}
          </span>
        </span>
        <span className="push" style={{ color: "var(--ui-accent-text)" }}>
          <IconDown size={14} />
        </span>
      </button>
    </div>
  );
}

/**
 * Видео в ленте — как в Discord: плашка с кнопкой «▶», по нажатию качается
 * и играет на месте, со своими кнопками браузера; «развернуть» — в
 * просмотрщик. Сразу не качается: в ленте их бывает много, а весят до 25 МБ.
 */
function VideoAttachment({ attachment, onExpand }: { attachment: MessageAttachment; onExpand?: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "failed">("idle");
  const player = useRef<HTMLVideoElement>(null);
  const alive = useRef(true);
  const box = imageBox(attachment.width ?? 16 * 40, attachment.height ?? 9 * 40);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  async function play() {
    setState("loading");
    try {
      const url = await mediaObjectUrl(attachment.id);
      // Строку успели увести из ленты — ссылка никому не нужна.
      if (!alive.current) {
        URL.revokeObjectURL(url);
        return;
      }
      setSrc(url);
      setState("idle");
    } catch {
      if (alive.current) setState("failed");
    }
  }

  return (
    <span className="attach-video" style={{ width: box.width, height: box.height }}>
      {src ? (
        <video ref={player} src={src} controls autoPlay playsInline />
      ) : (
        <button
          type="button"
          className="attach-video-cover"
          aria-label={`Смотреть ${attachment.filename}`}
          disabled={state === "loading"}
          onClick={() => void play()}
        >
          <span className="attach-video-play">{state === "loading" ? <Spinner /> : <IconPlay size={22} />}</span>
          <span className="attach-video-name ellipsis">
            {attachment.filename} · {fileSize(attachment.size)}
            {state === "failed" && " · не загрузилось"}
          </span>
        </button>
      )}
      {onExpand && (
        <button
          type="button"
          className="attach-video-expand"
          title="Развернуть"
          aria-label="Развернуть"
          onClick={() => {
            // Дважды одно видео не играет: в ленте — на паузу.
            player.current?.pause();
            onExpand();
          }}
        >
          <IconExpand size={14} />
        </button>
      )}
    </span>
  );
}

/** Звук в ленте — проигрыватель, как в Discord; качается по первому нажатию. */
/** Минуты и секунды — как на плеере голосового в Discord. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Голосовое — как в Discord: кнопка, волна и время. Запись грузится по первому
 * нажатию; сыгранная часть волны подсвечена, щелчок по волне переносит туда.
 * Время — из сообщения: у записи из браузера длительности в файле нет.
 */
function VoiceAttachment({ attachment }: { attachment: MessageAttachment }) {
  const bars = useMemo(() => decodeWave(attachment.waveform ?? ""), [attachment.waveform]);
  const total = (attachment.duration_ms ?? 0) / 1000;
  const player = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused" | "failed">("idle");
  const [at, setAt] = useState(0);
  const seekTo = useRef<number | null>(null);

  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  async function toggle(from?: number) {
    const audio = player.current;
    if (audio) {
      if (from !== undefined) audio.currentTime = from;
      if (audio.paused) void audio.play();
      else if (from === undefined) audio.pause();
      return;
    }
    if (state === "loading") return;
    setState("loading");
    seekTo.current = from ?? null;
    try {
      setSrc(await mediaObjectUrl(attachment.id));
    } catch {
      setState("failed");
    }
  }

  const played = total > 0 ? Math.min(1, at / total) : 0;
  return (
    <span className={`voice-note ${state}`}>
      <button
        type="button"
        className="voice-note-play"
        aria-label={state === "playing" ? "Пауза" : "Слушать голосовое"}
        onClick={() => void toggle()}
      >
        {state === "loading" ? <Spinner /> : state === "playing" ? <IconPause size={16} /> : <IconPlay size={16} />}
      </button>
      <span
        className="voice-note-wave"
        aria-hidden
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          void toggle(((event.clientX - box.left) / box.width) * total);
        }}
      >
        {bars.map((level, index) => (
          <span
            key={index}
            className={(index + 0.5) / bars.length <= played ? "on" : undefined}
            style={{ height: `${Math.max(12, Math.round(level * 100))}%` }}
          />
        ))}
      </span>
      <span className="voice-note-time">
        {state === "failed" ? "не загрузилось" : clock(state === "idle" ? total : total - at)}
      </span>
      {src && (
        <audio
          ref={player}
          src={src}
          autoPlay
          onLoadedMetadata={(event) => {
            if (seekTo.current !== null) event.currentTarget.currentTime = seekTo.current;
            seekTo.current = null;
          }}
          onPlay={() => setState("playing")}
          onPause={() => setState("paused")}
          onEnded={() => {
            setState("idle");
            setAt(0);
          }}
          onTimeUpdate={(event) => setAt(event.currentTarget.currentTime)}
          onError={() => setState("failed")}
        />
      )}
    </span>
  );
}

function AudioAttachment({ attachment }: { attachment: MessageAttachment }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  return (
    <span className="attach-audio">
      <span className="file-name ellipsis">{attachment.filename}</span>
      <span className="file-meta">{fileSize(attachment.size)}</span>
      {src ? (
        <audio src={src} controls autoPlay />
      ) : (
        <button
          type="button"
          className="attach-audio-play"
          aria-label={`Слушать ${attachment.filename}`}
          onClick={() =>
            void mediaObjectUrl(attachment.id).then(
              (url) => (alive.current ? setSrc(url) : URL.revokeObjectURL(url)),
              () => alive.current && setFailed(true),
            )
          }
        >
          <IconPlay size={16} /> {failed ? "не загрузилось" : "Слушать"}
        </button>
      )}
    </span>
  );
}

/**
 * Вложение-спойлер закрыто, пока его не нажмут, — как в Discord. Открывается
 * насовсем, как и спойлер в тексте: секрет уже показан.
 */
function Attachment({ attachment, gallery }: { attachment: MessageAttachment; gallery: MediaItem[] }) {
  const [shown, setShown] = useState(false);

  if (!attachment.spoiler || shown) return <AttachmentBody attachment={attachment} gallery={gallery} />;

  return (
    <span
      className="attach-spoiler"
      role="button"
      tabIndex={0}
      title="Показать спойлер"
      onClick={() => setShown(true)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setShown(true);
        }
      }}
    >
      <span className="attach-spoiler-body" aria-hidden>
        <AttachmentBody attachment={attachment} gallery={[]} />
      </span>
      <span className="attach-spoiler-tag">СПОЙЛЕР</span>
    </span>
  );
}

function AttachmentBody({ attachment, gallery }: { attachment: MessageAttachment; gallery: MediaItem[] }) {
  const isImage = attachment.inline && attachment.content_type.startsWith("image/");
  const open = () => {
    const at = gallery.findIndex((item) => item.id === attachment.id);
    // Открытый спойлер в общую галерею не входит: листанием его не раскрыть.
    if (at >= 0) useMediaViewer.getState().open(gallery, at);
    else if (isViewable(attachment)) useMediaViewer.getState().open([attachment], 0);
  };

  if (isImage) {
    const box = imageBox(attachment.width, attachment.height);
    return (
      <button
        type="button"
        className="attach-image"
        data-attachment={attachment.id}
        style={{ width: box.width, height: box.height }}
        title={attachment.description || `${attachment.filename} · ${attachment.width}×${attachment.height} · ${fileSize(attachment.size)}`}
        aria-label={`Открыть ${attachment.description || attachment.filename}`}
        onClick={open}
      >
        <FileImage id={attachment.id} alt={attachment.description || attachment.filename} width={box.width} height={box.height} />
      </button>
    );
  }

  if (attachment.waveform && attachment.duration_ms) {
    return <VoiceAttachment attachment={attachment} />;
  }

  if (attachment.inline && attachment.content_type.startsWith("video/")) {
    return <VideoAttachment attachment={attachment} onExpand={open} />;
  }

  if (attachment.inline && attachment.content_type.startsWith("audio/")) {
    return <AudioAttachment attachment={attachment} />;
  }

  /*
   * Текст показываем сами, и признак `inline` тут ни при чём.
   *
   * Он про то, как сервер отдаёт файл по ссылке: встроенным или вложением. У
   * текста он ложный, и это правильно — открывать `.svg` со скриптом внутри
   * страницей с нашего адреса нельзя. Но нарисовать содержимое текстом у себя
   * — совсем другое дело: React подставляет его как текст, а не как разметку,
   * и выполниться там нечему.
   *
   * До этой ветки текстовый файл попадал в карточку со словами «показ
   * отключён», и это читалось как «мы не можем», хотя могли.
   */
  if (attachment.content_type.startsWith("text/") && attachment.size <= PREVIEW_MAX) {
    return <TextAttachment attachment={attachment} />;
  }

  /*
   * Показывать на странице можно не всё, и решает это сервер: у вложения
   * приходит готовый признак. Клиент только рисует карточку — иначе защита
   * от исполняемой разметки зависела бы от его внимательности.
   */
  return (
    <button
      type="button"
      className={`file-card ${attachment.inline ? "" : "unsafe"}`}
      onClick={() => void downloadFile(attachment.id, attachment.filename)}
    >
      <span className="file-ext">{extension(attachment.filename, attachment.content_type)}</span>
      <span style={{ minWidth: 0 }}>
        <span className="file-name ellipsis">{attachment.filename}</span>
        <span className="file-meta">
          {attachment.inline
            ? fileSize(attachment.size)
            : `показ отключён — только скачиванием · ${fileSize(attachment.size)}`}
        </span>
      </span>
      <span className="push" style={{ color: "var(--ui-accent-text)" }}>
        <IconDown size={14} />
      </span>
    </button>
  );
}

/** Сообщение, которое ещё не ушло или не ушло совсем. */
export function PendingRow({
  content,
  attachments,
  failed,
  reason,
  onRetry,
  onDrop,
}: {
  content: string;
  /** Сколько приложено. Нужно, когда текста нет вовсе. */
  attachments: number;
  failed: boolean;
  /** Почему не ушло, если сервер сказал. */
  reason?: string;
  onRetry: () => void;
  onDrop: () => void;
}) {
  return (
    <div className="msg">
      <span className="msg-gutter" />
      <div className="msg-body">
        <div className="msg-text pending">
          {/*
            Сообщение бывает без единого слова — из одного файла. Пустая
            строка вместо него читалась бы как сбой, поэтому вместо текста
            говорим, что именно едет.
          */}
          {content || (
            <span className="muted">
              {attachments} {plural(attachments, "файл", "файла", "файлов")}
            </span>
          )}
          {!failed && <span className="msg-flag">· отправляется</span>}
        </div>
        {failed && (
          <div className="msg-failed">
            {reason ? `не ушло: ${reason}` : "не ушло"}
            <button type="button" className="link-btn" onClick={onRetry}>
              повторить
            </button>
            <button type="button" className="link-btn danger" onClick={onDrop}>
              убрать
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Свой вызов команды, пока бота не слышно: «думает…» — как в Discord, а через
 * три секунды без ответа — «Приложение не ответило». Видно только себе.
 */
export function CallRow({ call, onDrop }: { call: Call; onDrop: () => void }) {
  const name = call.bot.display_name || call.bot.username;
  return (
    <div className="msg">
      <span className="msg-gutter" />
      <div className="msg-body">
        {call.name && (
          <div className="msg-quote msg-called">
            <span className="msg-quote-turn">
              <IconCommand size={13} />
            </span>
            <span className="msg-quote-text ellipsis">{call.slash === false ? call.name : `/${call.name}`}</span>
          </div>
        )}
        {call.state === "silent" ? (
          <div className="msg-failed">
            Приложение не ответило
            <button type="button" className="link-btn danger" onClick={onDrop}>
              убрать
            </button>
          </div>
        ) : (
          <div className="msg-text pending" title={`Ответа ждём до ${RESPOND_WITHIN / 1000} с`}>
            {name} думает…
          </div>
        )}
      </div>
    </div>
  );
}

/** Откуда пересланное: автор, время, канал. Нажатие — к оригиналу, пока он есть. */
/**
 * Всё, что у сообщения под текстом: опрос, приглашения, стикер, превью ссылок,
 * пересланное и вложения. Одно на ленту и закреплённые — как в Discord, где
 * закреплённое показано тем же сообщением, а не пересказом.
 */
export function MessageExtras({
  message,
  editing = false,
  people,
  nameOf,
  mine,
  emojiOf,
  channelOf,
  roleOf,
  onOpenChannel,
  onOpenMessage,
  onOpenServer,
  onVote,
  onEndPoll,
  canRemoveEmbeds = false,
}: {
  message: Message;
  /** Текст правят: приглашения и превью ссылок прячутся до сохранения. */
  editing?: boolean;
  people: Map<Uuid, Named>;
  nameOf: (handle: string) => string | null;
  mine: string;
  emojiOf: (name: string) => CustomEmoji | null;
  channelOf: (id: string) => string | null;
  roleOf: (id: string) => string | null;
  onOpenChannel: (channelId: Uuid) => void;
  onOpenMessage?: (link: MessageLink) => void;
  onOpenServer?: (serverId: Uuid, channelId: Uuid | null) => void;
  /** Голос в опросе; нет — опрос только для чтения. */
  onVote?: (answerIds: Uuid[]) => Promise<void>;
  onEndPoll?: () => Promise<void>;
  /** Можно убрать превью ссылок крестиком: автору и тем, кто управляет сообщениями. */
  canRemoveEmbeds?: boolean;
}) {
  const me = fromSelf(useMe());
  // Ссылки-приглашения — карточками, не больше трёх; разбор — по тексту, не на каждую отрисовку.
  const invites = useMemo(() => readInviteCodes(message.content).slice(0, 3), [message.content]);
  /** Превью убрали крестиком — до события с сервера. */
  const [embedsGone, setEmbedsGone] = useState(false);

  return (
    <>
      {/*
        Пустое сообщение без вложений и пересылки бывает одно — стикер,
        которого больше нет на сервере.
      */}
      {!message.content &&
        !message.sticker &&
        !message.poll &&
        !message.forwarded &&
        !message.embeds?.length &&
        !message.components?.length &&
        message.attachments.length === 0 && <div className="msg-gone">Стикер удалён</div>}

      {message.poll && (
        <PollCard
          poll={message.poll}
          onVote={onVote}
          onEnd={onEndPoll}
          voters={(answerId) => api.messages.pollVoters(message.channel_id, message.id, answerId)}
        />
      )}

      {/* Ссылки-приглашения — карточками, не больше трёх на сообщение. */}
      {!editing &&
        invites.map((code) => <InviteCard key={code} code={code} onOpenServer={onOpenServer} />)}

      {/* Стикер — картинкой под текстом, без рамки: как в Discord. */}
      {message.sticker && (
        <FileImage
          id={message.sticker.file_id}
          alt={message.sticker.name}
          className="msg-sticker"
          width={160}
          height={160}
        />
      )}

      {!editing && message.content && !message.suppress_embeds && !embedsGone && (
        <LinkEmbeds
          content={message.content}
          onRemove={
            canRemoveEmbeds
              ? () => {
                  // Себе событие о правке не придёт — прячем сразу.
                  setEmbedsGone(true);
                  void api.messages.suppressEmbeds(message.channel_id, message.id).catch(() => setEmbedsGone(false));
                }
              : undefined
          }
        />
      )}

      {/* Вставки бота — карточками под текстом, как в Discord. */}
      <BotEmbeds
        message={message}
        markup={(text) => (
          <Markup
            text={text}
            nameOf={nameOf}
            mine={mine}
            emojiOf={emojiOf}
            channelOf={channelOf}
            roleOf={roleOf}
            onOpenChannel={onOpenChannel}
            onOpenMessage={onOpenMessage}
          />
        )}
      />

      {/* Пересланное — снимок оригинала, как в Discord; подпись ведёт к нему. */}
      {message.forwarded && (
        <div className="msg-forward">
          <span className="msg-forward-mark">
            <IconForward size={12} /> Переслано
          </span>
          {message.forwarded.content && (
            <div className="msg-forward-text">
              <Markup
                text={message.forwarded.content}
                nameOf={nameOf}
                mine={mine}
                emojiOf={emojiOf}
                channelOf={channelOf}
                roleOf={roleOf}
                onOpenChannel={onOpenChannel}
                onOpenMessage={onOpenMessage}
              />
            </div>
          )}
          {message.forwarded.attachments.length > 0 && (
            <div className="attach">
              {message.forwarded.attachments.map((attachment) => (
                <Attachment
                  key={attachment.id}
                  attachment={attachment}
                  gallery={message.forwarded!.attachments.filter((item) => isViewable(item) && !item.spoiler)}
                />
              ))}
            </div>
          )}
          <ForwardSource forwarded={message.forwarded} people={people} me={me} channelOf={channelOf} onOpenMessage={onOpenMessage} />
        </div>
      )}

      {message.attachments.length > 0 && (
        <div className="attach">
          {message.attachments.map((attachment) => (
            <Attachment key={attachment.id} attachment={attachment} gallery={message.attachments.filter((item) => isViewable(item) && !item.spoiler)} />
          ))}
        </div>
      )}

      {/* Кнопки и списки бота — последними, под всем, как в Discord. */}
      {!editing && <BotComponents message={message} emojiOf={emojiOf} />}
    </>
  );
}

/**
 * Что вызвали — над ответом бота: команда поля ввода — с косой чертой, команда
 * меню — без неё; нажатие и окно — «взаимодействует».
 */
function calledText(interaction: NonNullable<Message["interaction"]>): string {
  if (interaction.type === 3 || interaction.type === 5)
    return interaction.name ? `взаимодействует с /${interaction.name}` : "взаимодействует с приложением";
  return interaction.command_type === 2 || interaction.command_type === 3
    ? `использует «${interaction.name}»`
    : `использует /${interaction.name}`;
}

function ForwardSource({
  forwarded,
  people,
  me,
  channelOf,
  onOpenMessage,
}: {
  forwarded: MessageForward;
  people: Map<Uuid, Named>;
  me: ReturnType<typeof fromSelf>;
  channelOf: (id: string) => string | null;
  onOpenMessage?: (link: MessageLink) => void;
}) {
  const who = forwarded.author ? livePerson(forwarded.author, people, me).name : "автор удалён";
  const where = forwarded.channel_id ? channelOf(forwarded.channel_id) : null;
  const sourceId = forwarded.source_id;
  const channelId = forwarded.channel_id;
  const open = sourceId && channelId && onOpenMessage;

  return (
    <button
      type="button"
      className="msg-forward-from"
      disabled={!open}
      title={open ? "Перейти к оригиналу" : "Оригинала больше нет"}
      onClick={() =>
        open && onOpenMessage({ serverId: forwarded.server_id, channelId, messageId: sourceId })
      }
    >
      {who} · {dayTime(forwarded.created)}
      {where && ` · #${where}`}
    </button>
  );
}

const WELCOMES = [
  (name: string) => `Встречайте: ${name}!`,
  (name: string) => `${name} — теперь с нами.`,
  (name: string) => `На сервере пополнение: ${name}.`,
  (name: string) => `Поздоровайтесь — здесь ${name}.`,
];

function welcome(name: string, id: string): string {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return WELCOMES[hash % WELCOMES.length](name);
}

/** Сколько длился звонок — словами, как в Discord: короче минуты — «несколько секунд». */
function callLength(started: string, ended: string): string {
  const minutes = Math.round((Date.parse(ended) - Date.parse(started)) / 60_000);
  if (minutes < 1) return "несколько секунд";
  if (minutes < 60) return `${minutes} ${plural(minutes, "минута", "минуты", "минут")}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} ${plural(hours, "час", "часа", "часов")}${rest ? ` ${rest} мин` : ""}`;
}

/**
 * «Присоединиться к звонку» — тому, кого в звонке нет. Входит не строка: звонок
 * держит окно, просьба уходит через стор (`requestCall`).
 */
function JoinCallLink({ channelId, meId }: { channelId: string; meId: string }) {
  const inside = useCatalogue((state) => (state.calls[channelId] ?? []).some((member) => member.id === meId));
  const requestCall = useCatalogue((state) => state.requestCall);
  if (inside) return null;
  return (
    <>
      {" — "}
      <button type="button" className="msg-call-join" onClick={() => requestCall(channelId)}>
        Присоединиться к звонку
      </button>
    </>
  );
}

